import {MenuItem} from "@mui/material";
import {red} from "@mui/material/colors";
import {orderByDescending} from "nate-react-api-helpers";
import React, {useCallback, useContext, useEffect, useState} from "react";
import {CategoryContext} from "./CategoryProvider";
import {PrepNoOptions} from "./preps/PrepNoOptions";
import {DoorAndFrame} from "./preps/DoorAndFrame";
import {PairOnly} from "./preps/PairOnly";
import {AllProps} from "./preps/AllProps";
import {OneDropdown} from "./preps/OneDropdown";
import {Category, Product} from "../../../../api/Products";

export const customPrep = "Custom"
export const noPrep = "No Prep"
export const contHingePrep = "Cont Hinge Reinforcing"
export const followCloserRule = "**Follow closer rule**"
export const locksetASAPrep = "ASA"
export const dbStrikePrep = "DB Strike"
export const dbPrep = "DB Prep"
export const blankPrep = "BLANK"
export const asPerTemplate = "**As per template**"

export const noPrepOpt: PrepOption = {
    singleFramePrep: noPrep,
    singleActiveDoorPrep: noPrep,
    pairFramePrep: noPrep,
    pairActiveDoorPrep: noPrep,
    pairInactiveDoorPrep: noPrep,
    pairBothActiveDoorPrep: noPrep,
}

function tmplEq(a?: number | null, b?: number | null): boolean {
    if(!!a && a === b) return true;
    if(!!b) return false;

    return true;
}

export function prepEq(a: PrepOption, b: PrepOption): boolean {
    for(const key of Object.keys(a) as (keyof PrepOption)[]) {
        if(a[key] !== b[key]) {
            console.log("key", key, a[key], b[key])
        }
    }

    if(a.singleFramePrep !== b.singleFramePrep) return false;
    if(a.singleActiveDoorPrep !== b.singleActiveDoorPrep) return false;
    if(a.pairFramePrep !== b.pairFramePrep) return false;
    if(a.pairActiveDoorPrep !== b.pairActiveDoorPrep) return false;
    if(a.pairInactiveDoorPrep !== b.pairInactiveDoorPrep) return false;
    if(a.pairBothActiveDoorPrep !== b.pairBothActiveDoorPrep) return false;

    if(!tmplEq(a.singleFramePrepCustomTemplate, b.singleFramePrepCustomTemplate)) return false;
    if(!tmplEq(a.singleActiveDoorPrepCustomTemplate, b.singleActiveDoorPrepCustomTemplate)) return false;
    if(!tmplEq(a.pairFramePrepCustomTemplate, b.pairFramePrepCustomTemplate)) return false;
    if(!tmplEq(a.pairActiveDoorPrepCustomTemplate, b.pairActiveDoorPrepCustomTemplate)) return false;
    if(!tmplEq(a.pairInactiveDoorPrepCustomTemplate, b.pairInactiveDoorPrepCustomTemplate)) return false;
    if(!tmplEq(a.pairBothActiveDoorPrepCustomTemplate, b.pairBothActiveDoorPrepCustomTemplate)) return false;

    return true;
}

export type OptionGroup = {
    categoryPath: string[];
    special?: "single-drop-down" | "door-and-frame-drop-down" | "no-options" | "pair-only";

    singleFramePreps: string[];
    singleFramePrepsCustom?: string[];
    pairFramePreps: string[];
    pairFramePrepsCustom?: string[];
    singleActiveDoorPreps: string[];
    singleActiveDoorPrepsCustom?: string[];
    pairActiveDoorPreps: string[];
    pairActiveDoorPrepsCustom?: string[];
    pairInactiveDoorPreps: string[];
    pairInactiveDoorPrepsCustom?: string[];
    pairBothActiveDoorPreps?: string[];
    pairBothActiveDoorPrepsCustom?: string[];
}

export type PrepOption = {
    needsTemplate?: boolean;

    singleFramePrep: string;
    singleFramePrepCustomTemplate?: number | null;
    singleFramePrepCustomTemplateName?: string | null;
    singleActiveDoorPrep: string;
    singleActiveDoorPrepCustomTemplate?: number | null;
    singleActiveDoorPrepCustomTemplateName?: string | null;

    pairFramePrep: string;
    pairFramePrepCustomTemplate?: number | null;
    pairFramePrepCustomTemplateName?: string | null;
    pairActiveDoorPrep: string;
    pairActiveDoorPrepCustomTemplate?: number | null;
    pairActiveDoorPrepCustomTemplateName?: string | null;
    pairInactiveDoorPrep: string;
    pairInactiveDoorPrepCustomTemplate?: number | null;
    pairInactiveDoorPrepCustomTemplateName?: string | null;
    pairBothActiveDoorPrep: string;
    pairBothActiveDoorPrepCustomTemplate?: number | null;
    pairBothActiveDoorPrepCustomTemplateName?: string | null;
}

// hinge config keys, kept in sync with the elevation tool's hinge model.
// see pages/project/shopdrawing/elevationTool/model/hardware/MdHinges.ts
export const hinge45Std = "4.5\" STD"
export const hinge5Std = "5\" STD"
export const hinge45HW = "4.5\" HW"
export const hinge5HW = "5\" HW"

export function isHingePrepKey(key: string) {
    switch(key) {
        case hinge45Std:
        case hinge5Std:
        case hinge45HW:
        case hinge5HW:
            return true;
        default:
            return false;
    }
}

function makePairOnly2(catPaths: string[][], opts: {
    frame?: {
        preps?: string[];
    },
    active?: {
        preps?: string[];
    },
    inActive?: {
        preps?: string[];
    }
}) {
    catPaths.forEach(c => {
        addOption({
            categoryPath: c,
            special: "pair-only",
            pairFramePreps: opts.frame?.preps || [],
            pairActiveDoorPreps: opts.active?.preps || [],
            pairInactiveDoorPreps: opts.inActive?.preps || [],
            singleFramePreps: [],
            singleActiveDoorPreps: [],
        });
    });
}

function makeSingleDropDown2(catPath: string[][], opts: {
    preps?: string[];
    customPreps?: string[];
}) {
    return catPath.map(p => makeSingleDropDown(p, opts))
}

function makeSingleDropDown(catPath: string[], opts: {
    preps?: string[];
    customPreps?: string[];
}): OptionGroup {
    const stdPreps = opts.preps || [];
    const customPreps = opts.customPreps || [];

    return addOption({
        categoryPath: catPath,

        special: "single-drop-down",

        singleFramePreps: stdPreps,
        singleFramePrepsCustom: customPreps,

        pairFramePreps: stdPreps,
        pairFramePrepsCustom: customPreps,

        singleActiveDoorPreps: stdPreps,
        singleActiveDoorPrepsCustom: customPreps,

        pairActiveDoorPreps: stdPreps,
        pairActiveDoorPrepsCustom: customPreps,

        pairInactiveDoorPreps: stdPreps,
        pairInactiveDoorPrepsCustom: customPreps,
    });
}

function makeDoorAndFrameDropDown2(catPath: string[][], opts: {
    door?: {
        preps?: string[];
    },
    frame?: {
        preps?: string[];
    }
}): OptionGroup[] {
    return catPath.map(c => makeDoorAndFrameDropDown(c, opts))
}

function makeDoorAndFrameDropDown(catPath: string[], opts: {
    door?: {
        preps?: string[];
    },
    frame?: {
        preps?: string[];
    }
}): OptionGroup {
    const stdDoorPreps = opts.door?.preps || [];
    const stdFramePreps = opts.frame?.preps || [];

    return addOption({
        categoryPath: catPath,

        special: "door-and-frame-drop-down",

        singleFramePreps: stdFramePreps,
        pairFramePreps: stdFramePreps,

        singleActiveDoorPreps: stdDoorPreps,
        pairActiveDoorPreps: stdDoorPreps,

        pairInactiveDoorPreps: stdDoorPreps,
    });
}

function makeDetailed(catPath: string[], opts: {
    activeDoor?: {
        preps?: string[];
    },
    inactiveDoorAndSingleFrame?: {
        preps?: string[];
    },
    pairFrame?: {
        preps?: string[];
    },
    bothActive?: {
        preps?: string[];
    }
}): OptionGroup {
    return addOption({
        categoryPath: catPath,
        singleActiveDoorPreps: opts.activeDoor?.preps || [],
        singleFramePreps: opts.inactiveDoorAndSingleFrame?.preps || [],
        pairFramePreps: opts.pairFrame?.preps || [],
        pairActiveDoorPreps: opts.activeDoor?.preps || [],
        pairInactiveDoorPreps: opts.inactiveDoorAndSingleFrame?.preps || [],
        pairBothActiveDoorPreps: opts.bothActive?.preps,
    });
}

function makeDetailed2(catPath: string[], opts?: {
    allDoors?: {
        preps?: string[],
    },
    allFrames?: {
        preps?: string[];
    },
    bothActive?: {
        preps?: string[];
    }
}): OptionGroup {
    const doorPreps = opts?.allDoors?.preps || [];
    const framePreps = opts?.allFrames?.preps || [];

    return addOption({
        categoryPath: catPath,
        singleActiveDoorPreps: doorPreps,
        pairActiveDoorPreps: doorPreps,
        pairInactiveDoorPreps: doorPreps,
        singleFramePreps: framePreps,
        pairFramePreps: framePreps,
        pairBothActiveDoorPreps: opts?.bothActive?.preps,
    });
}

const options: OptionGroup[] = []

const customPrepKeys = [
    "singleFramePrepsCustom",
    "pairFramePrepsCustom",
    "singleActiveDoorPrepsCustom",
    "pairActiveDoorPrepsCustom",
    "pairInactiveDoorPrepsCustom",
    "pairBothActiveDoorPrepsCustom",
] as const;

// every prep dropdown offers a Custom option, so make sure customPrep is in each
// custom list before the group is registered
function addOption(group: OptionGroup): OptionGroup {
    for(const key of customPrepKeys) {
        const list = group[key] || [];
        if(!list.includes(customPrep)) {
            list.push(customPrep);
        }
        group[key] = list;
    }

    options.push(group);
    return group;
}

makeSingleDropDown(["Hinges", "Standard Hinge"], {
    preps: [hinge45Std, hinge5Std],
});
    makeSingleDropDown(["Hinges", "Exterior Hinge"], {
        preps: [hinge45Std, hinge5Std],
    });
    makeSingleDropDown(["Hinges", "Heavy Weight Hinge"], {
        preps: [hinge45HW, hinge5HW],
    });
    makeSingleDropDown(["Hinges", "Continuous Hinge"], {
        customPreps: [contHingePrep]
    });
    makeSingleDropDown(["Hinges", "Spring Hinge"], {
        preps: [hinge45Std, hinge5Std, hinge45HW, hinge5HW],
    });
    makeSingleDropDown(["Hinges", "Power Transfer Hinge"], {});
    makeSingleDropDown(["Hinges", "Specialty Hinge"], {
        preps: [hinge45Std, hinge5Std, hinge45HW, hinge5HW],
    });
    makeDoorAndFrameDropDown(["Hinges", "Specialty Hinge", "Half Mortise Hinge"], {
        frame: {
            preps: [noPrep],
        },
        door: {
            preps: [hinge45Std, hinge5Std],
        }
    });
    makeDoorAndFrameDropDown(["Hinges", "Specialty Hinge", "Half Surface Hinge"], {
        frame: {
            preps: [hinge45Std, hinge5Std],
        },
        door: {
            preps: [noPrep],
        },
    });
    makeDoorAndFrameDropDown(["Hinges", "Specialty Hinge", "Double Acting Hinge"], {
        frame: {
            preps: [noPrep, "Frame Reinforcing"],
        },
        door: {
            preps: [hinge45Std, hinge5Std, hinge45HW, hinge5HW],
        },
    });
    makeDoorAndFrameDropDown(["Mortar Box"], {
        frame: {
            preps: [noPrep],
        },
        door: {
            preps: [noPrep],
        },
    });
    makeDetailed(["Locksets", "Cylindrical Lockset"], {
        activeDoor: {
            preps: ["161"],
        },
        inactiveDoorAndSingleFrame: {
            preps: [locksetASAPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Locksets", "Cylindrical Lockset", "Dummy Lever"], {
        activeDoor: {
            preps: [noPrep, "161"],
        },
        inactiveDoorAndSingleFrame: {
            preps: [noPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Locksets", "Cylindrical Lockset", "Dummy Knob"], {
        activeDoor: {
            preps: [noPrep, "161"],
        },
        inactiveDoorAndSingleFrame: {
            preps: [noPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Locksets", "Mortise Lockset"], {
        activeDoor: {
            preps: [],
        },
        inactiveDoorAndSingleFrame: {
            preps: [locksetASAPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Locksets", "Mortise Multi-Point Lock"], {
        activeDoor: {
            preps: [],
        },
        inactiveDoorAndSingleFrame: {
            preps: [],
        },
        pairFrame: {
            preps: [],
        }
    });
    makeDetailed(["Locksets", "Electronic Lockset"], {
        activeDoor: {
            preps: ["161"],
        },
        inactiveDoorAndSingleFrame: {
            preps: [locksetASAPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Locksets", "Mortise Deadlatch"], {
        activeDoor: {
            preps: [noPrep],
        },
        inactiveDoorAndSingleFrame: {
            preps: [noPrep],
        },
        pairFrame: {
            preps: [noPrep],
        },
        bothActive: {
            preps: [noPrep]
        }
    });
    makeDetailed(["Locksets", "Patch Locks"], {
        activeDoor: {},
        inactiveDoorAndSingleFrame: {},
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Locksets", "Tubular Lockset"], {
        activeDoor: {
            preps: ["161"],
        },
        inactiveDoorAndSingleFrame: {
            preps: [locksetASAPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Handlesets"], {
        activeDoor: {},
        inactiveDoorAndSingleFrame: {
            preps: [locksetASAPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Deadbolts", "Deadbolt"], {
        activeDoor: {
            preps: [dbPrep],
        },
        inactiveDoorAndSingleFrame: {
            preps: [dbStrikePrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Deadbolts", "Mortise Deadbolt"], {
        activeDoor: {},
        inactiveDoorAndSingleFrame: {
            preps: [noPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Deadbolts", "Mortise Deadlock"], {
        activeDoor: {},
        inactiveDoorAndSingleFrame: {
            preps: [noPrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    addOption({
        categoryPath: ["Deadbolts", "Mortise Deadlock Faceplate"],
        special: "no-options",
        singleFramePreps: [],
        pairFramePreps: [],
        singleActiveDoorPreps: [],
        pairActiveDoorPreps: [],
        pairInactiveDoorPreps: [],
    });
    makeDetailed(["Deadbolts", "Electronic Deadbolt"], {
        activeDoor: {
            preps: [dbPrep],
        },
        inactiveDoorAndSingleFrame: {
            preps: [dbStrikePrep],
        },
        pairFrame: {
            preps: [noPrep],
        }
    });
    makeDetailed(["Exit Devices", "Rim Exit Device"], {
        activeDoor: {
            preps: ["RP"],
        },
        inactiveDoorAndSingleFrame: {
            preps: ["RP"],
        },
        pairFrame: {
            preps: [noPrep],
        },
        bothActive: {
            preps: ["RP"],
        }
    });
    makeDetailed2(["Exit Devices", "Surface Vertical Rod Exit Device"], {
        allDoors: {
            preps: ["RP"],
        },
        allFrames: {
            preps: ["Vert Rod Reinforcing"],
        },
        bothActive: {
            preps: ["RP"],
        }
    });
    addOption({
        categoryPath: ["Exit Devices", "Concealed Vertical Rod Exit Device"],
        singleFramePreps: [],
        pairFramePreps: [],
        singleActiveDoorPreps: [],
        pairActiveDoorPreps: [],
        pairInactiveDoorPreps: [],
        pairBothActiveDoorPreps: [],
    });
    addOption({
        categoryPath: ["Exit Trims", "Lever Trims"],
        singleFramePreps: [noPrep],
        pairFramePreps: [noPrep],
        singleActiveDoorPreps: ["2 1/8\" Borehole"],
        pairActiveDoorPreps: ["2 1/8\" Borehole"],
        pairInactiveDoorPreps: ["RP"],
        pairBothActiveDoorPreps: ["2 1/8\" Borehole"],
    });
    addOption({
        categoryPath: ["Exit Trims", "Thumb-latch Trims"],
        singleFramePreps: [noPrep],
        singleActiveDoorPreps: [],
        pairFramePreps: [noPrep],
        pairActiveDoorPreps: [],
        pairInactiveDoorPreps: ["RP"],
        pairBothActiveDoorPreps: [],
    });
    addOption({
        categoryPath: ["Exit Trims", "Cylinder Trims"],
        singleFramePreps: [noPrep],
        singleActiveDoorPreps: [],
        pairFramePreps: [noPrep],
        pairActiveDoorPreps: [],
        pairInactiveDoorPreps: ["RP"],
        pairBothActiveDoorPreps: [],
    });
    addOption({
        categoryPath: ["Exit Trims", "Knob Trims"],
        singleFramePreps: [noPrep],
        pairFramePreps: [noPrep],
        singleActiveDoorPreps: ["2 1/8\" Borehole"],
        pairActiveDoorPreps: ["2 1/8\" Borehole"],
        pairInactiveDoorPreps: ["RP"],
        pairBothActiveDoorPreps: ["2 1/8\" Borehole"],
    });
    addOption({
        categoryPath: ["Electric Strikes", "Electric Strike"],
        singleFramePreps: ["RP", locksetASAPrep],
        singleActiveDoorPreps: [noPrep],
        pairFramePreps: [noPrep],
        pairActiveDoorPreps: [noPrep],
        pairInactiveDoorPreps: [noPrep, "RP & Raceway", "ASA & Raceway"],
        pairInactiveDoorPrepsCustom: ["Custom & Raceway"],
    });
    addOption({
        categoryPath: ["Electric Strikes", "Concealed Electric Strike"],
        singleFramePreps: [locksetASAPrep],
        singleActiveDoorPreps: [noPrep],
        pairFramePreps: [noPrep],
        pairActiveDoorPreps: [noPrep],
        pairInactiveDoorPreps: ["ASA & Raceway"],
        pairInactiveDoorPrepsCustom: ["Custom & Raceway"],
    });
    addOption({
        categoryPath: ["Electric Strikes", "Surface Electric Strike"],
        singleFramePreps: ["RP"],
        singleActiveDoorPreps: [noPrep],
        pairFramePreps: [noPrep],
        pairActiveDoorPreps: [noPrep],
        pairInactiveDoorPreps: [],
        pairInactiveDoorPrepsCustom: ["Custom & Raceway"],
    });
    addOption({
        categoryPath: ["Electric Strikes", "Header Electric Strike"],
        singleFramePreps: [],
        singleActiveDoorPreps: [noPrep],
        pairFramePreps: [],
        pairActiveDoorPreps: [noPrep],
        pairInactiveDoorPreps: [noPrep],
    });
    makeDoorAndFrameDropDown(["Closers", "Surface Closer"], {
        frame: {
            preps: [followCloserRule]
        },
        door: {
            preps: [followCloserRule]
        }
    });
    makeDoorAndFrameDropDown(["Closers", "Electronic Closer"], {
        frame: {
            preps: [followCloserRule]
        },
        door: {
            preps: [followCloserRule]
        }
    });
    makeDoorAndFrameDropDown(["Closers", "Track Closer"], {
        frame: {
            preps: [followCloserRule]
        },
        door: {
            preps: [followCloserRule]
        }
    });
    makeDoorAndFrameDropDown(["Closers", "Concealed Closer"], {
        frame: {},
        door: {}
    });
    makeDoorAndFrameDropDown2([
        ["Closers", "Closer Arms"],
        ["Closers", "Drop Plate"],
    ], {
        frame: {
            preps: [noPrep]
        },
        door: {
            preps: [noPrep]
        }
    });
    makeDoorAndFrameDropDown2([
        ["Overhead Stops", "Surface OH Stop"],
        ["Overhead Stops", "Surface OH Holder"],
    ], {
        frame: {
            preps: [followCloserRule]
        },
        door: {
            preps: [followCloserRule]
        }
    });
    makeDoorAndFrameDropDown2([
        ["Overhead Stops", "Concealed OH Stop"],
        ["Overhead Stops", "Concealed OH Holder"],
    ], {
        frame: {},
        door: {}
    });
    makeDoorAndFrameDropDown(["Overhead Stops", "Chain Stop"], {
        frame: {
            preps: [followCloserRule]
        },
        door: {
            preps: [followCloserRule]
        }
    });
    makeSingleDropDown2([
        ["Plates & Guards", "Kick Plate"],
        ["Plates & Guards", "Mop Plate"],
        ["Plates & Guards", "Push Plate"],
        ["Plates & Guards", "Protection Plates"],
        ["Plates & Guards", "Guards"],
    ], {
        preps: [noPrep],
    });
    makeSingleDropDown(["Auto Operators & Components", "Surface Automatic Operator"], {
        preps: [followCloserRule],
    });

    makeSingleDropDown2([
        ["Auto Operators & Components", "Surface Automatic Operator"],
        ["Auto Operators & Components", "Actuator"],
        ["Auto Operators & Components", "Bollard"],
        ["Auto Operators & Components", "Relay"],
        ["Auto Operators & Components", "BF Washroom Kit"],
        ["Auto Operators & Components", "Audible Alarm Kit"],
        ["Auto Operators & Components", "Push to Lock Kit"],
        ["Auto Operators & Components", "Transmitter"],
    ], {
        preps: [noPrep]
    });
    makeDoorAndFrameDropDown2([
        ["Pulls & Bars", "Door Pull"],
        ["Pulls & Bars", "Push/Pull"],
        ["Pulls & Bars", "Push Bar"],
    ], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Pulls & Bars", "Flush Pull"], {
        door: {},
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Pulls & Bars", "Pull Plate"], {
        door: {
            preps: [noPrep]
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Pulls & Bars", "Edge Pull"], {
        door: {},
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Pulls & Bars", "Guard Bar"], {
        door: {
            preps: [noPrep]
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Electronic Components", "Power Transfer"], {
        door: {
            preps: ["Raceway"],
        },
        frame: {}
    });
    makeDoorAndFrameDropDown(["Electronic Components", "Power Supply"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Electronic Components", "Wiring Harness"], {
        door: {
            preps: ["Raceway"],
        },
        frame: {}
    });
    makeDoorAndFrameDropDown(["Electronic Components", "Card Reader"], {
        door: {
            preps: ["Raceway", noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Electronic Components", "Door Contact"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeSingleDropDown2([
        ["Cylinders & Keying", "Rim Cylinder"],
        ["Cylinders & Keying", "Mortise Cylinder"],
        ["Cylinders & Keying", "KIK Cylinder"],
        ["Cylinders & Keying", "Keying"],
    ], {
        preps: [noPrep]
    });
    makePairOnly2([
        ["Bolts", "Surface Bolt"],
    ], {
        frame: {
            preps: [noPrep]
        },
        active: {
            preps: [noPrep]
        },
        inActive: {
            preps: [noPrep],
        }
    });
    makePairOnly2([
        ["Bolts", "Manual Flush Bolt"],
        ["Bolts", "Auto Flush Bolt"],
        ["Bolts", "Self Latching Bolt"],
        ["Bolts", "Combo Flush Bolt"],
    ], {
        frame: {
            preps: ["FB Strike", noPrep],
        },
        active: {
            preps: [noPrep]
        },
        inActive: {
            preps: ["FB Prep"],
        }
    });
    makeDoorAndFrameDropDown(["Coordinators & Catches", "Coordinator"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Coordinators & Catches", "Filler Bar"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Coordinators & Catches", "Roller Catch"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Coordinators & Catches", "Magnetic Catch"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeSingleDropDown2([
        ["Stops & Holders", "Floor Stop"],
        ["Stops & Holders", "Wall Stop"],
        ["Stops & Holders", "Hinge Pin Stop"],
        ["Stops & Holders", "Baseboard Stop"],
        ["Stops & Holders", "Fantom Stop"],
        ["Stops & Holders", "Kickstand Holder"],
        ["Stops & Holders", "Roller Stop"],
    ], {
        preps: [noPrep],
    });
    makeSingleDropDown2([
        ["Stops & Holders", "Magnetic Holder"],
        ["Stops & Holders", "Electromagnetic Holder"],
    ], {
        preps: [noPrep],
    });

    makeDoorAndFrameDropDown2([
        ["Weatherstripping & Trims", "Thresholds"],
        ["Weatherstripping & Trims", "Weatherstripping"],
        ["Weatherstripping & Trims", "Sweeps"],
    ], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown2([
        ["Weatherstripping & Trims", "Automatic Door Bottoms"],
    ], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown2([
        ["Weatherstripping & Trims", "Astragals & Mullions"],
        ["Weatherstripping & Trims", "Drip Edges & Top Caps"],
    ], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown2([
        ["Sliding Door Hardware", "Tracks"],
        ["Sliding Door Hardware", "Sliding Hardware"],
        ["Sliding Door Hardware", "Sliding Hardware Kits"],
        ["Sliding Door Hardware", "Pocket Door Frame"],
    ], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
    makeDoorAndFrameDropDown(["Misc Hardware", "Door Viewer"], {
        door: {
            preps: [noPrep],
        },
        frame: {
            preps: [noPrep],
        }
    });
makeDoorAndFrameDropDown2([
    ["Misc Hardware", "Door Guards"],
    ["Misc Hardware", "Door Bumper"]
], {
    door: {
        preps: [noPrep],
    },
    frame: {
        preps: [noPrep],
    }
})
makeDoorAndFrameDropDown2([
    ["Parts", "Strikes"],
    ["Parts", "Latches"]
], {
    door: {
        preps: [noPrep],
    },
    frame: {
        preps: [noPrep],
    }
})
addOption({
    categoryPath: ["Louvers"],
    singleFramePreps: [noPrep],
    singleActiveDoorPreps: [noPrep],
    pairFramePreps: [noPrep],
    pairActiveDoorPreps: [noPrep],
    pairInactiveDoorPreps: [noPrep],
});

export const ZAstragal = "Z-Astragal"

addOption({
    categoryPath: ["Weatherstripping & Trims", "Astragals & Mullions", ZAstragal],
    singleFramePreps: [noPrep],
    singleActiveDoorPreps: [noPrep],
    pairFramePreps: [noPrep],
    pairActiveDoorPreps: [noPrep],
    pairInactiveDoorPreps: [noPrep, ZAstragal],
});


function prefixMatch(value: string[], prefix: string[]) {

    for (let i = 0; i < prefix.length && i < value.length; i++) {
        if (value[i] !== prefix[i]) return i;
    }

    return Math.min(value.length, prefix.length);
}

function findGroup(catPath: string[]) {
    // longest prefix match
    const matches = options.filter(o => prefixMatch(catPath, o.categoryPath) > 0);
    if (matches.length === 0) return null;

    orderByDescending(matches, s => prefixMatch(catPath, s.categoryPath));
    return matches[0];
}

export function usePrepMenuValue(props: {
    category: number | null;
    isCreate: boolean;
    edit?: Product;
}) {
    const edit = props.edit;
    const [value, setValue] = useState<PrepOption>(edit ? {
        pairFramePrep: edit.hwPrepPairFrame,
        pairFramePrepCustomTemplate: edit.hwPrepPairFrameCustomTemplate,
        pairFramePrepCustomTemplateName: edit.hwPrepPairFrameCustomTemplateName,
        pairActiveDoorPrep: edit.hwPrepPairActiveDoor,
        pairActiveDoorPrepCustomTemplate: edit.hwPrepPairActiveDoorCustomTemplate,
        pairActiveDoorPrepCustomTemplateName: edit.hwPrepPairActiveDoorCustomTemplateName,
        pairInactiveDoorPrep: edit.hwPrepPairInactiveDoor,
        pairInactiveDoorPrepCustomTemplate: edit.hwPrepPairInactiveDoorCustomTemplate,
        pairInactiveDoorPrepCustomTemplateName: edit.hwPrepPairInactiveDoorCustomTemplateName,
        pairBothActiveDoorPrep: edit.hwPrepPairBothActiveDoor,
        pairBothActiveDoorPrepCustomTemplate: edit.hwPrepPairBothActiveDoorCustomTemplate,
        pairBothActiveDoorPrepCustomTemplateName: edit.hwPrepPairBothActiveDoorCustomTemplateName,

        singleFramePrep: edit.hwPrepSingleFrame,
        singleFramePrepCustomTemplate: edit.hwPrepSingleFrameCustomTemplate,
        singleFramePrepCustomTemplateName: edit.hwPrepSingleFrameCustomTemplateName,
        singleActiveDoorPrep: edit.hwPrepSingleActiveDoor,
        singleActiveDoorPrepCustomTemplate: edit.hwPrepSingleActiveDoorCustomTemplate,
        singleActiveDoorPrepCustomTemplateName: edit.hwPrepSingleActiveDoorCustomTemplateName,
    } : noPrepOpt);

    const catCtx = useContext(CategoryContext);
    const categories = catCtx.categories;

    const catPath = props.category ? catCtx.getNamePath(props.category) : [];
    const group = findGroup(catPath) || defaultGroup;

    useEffect(() => {
        if(!props.isCreate) return;

        const obj = Object.assign({}, value);
        let updated = false;

        if(autoFix(obj, group.singleActiveDoorPreps, group.singleActiveDoorPrepsCustom, "singleActiveDoorPrep", "singleActiveDoorPrepCustomTemplate", "singleActiveDoorPrepCustomTemplateName")) {
            updated = true;
        }

        if(autoFix(obj, group.singleFramePreps, group.singleFramePrepsCustom, "singleFramePrep", "singleFramePrepCustomTemplate", "singleFramePrepCustomTemplateName")) {
            updated = true;
        }

        if(autoFix(obj, group.pairActiveDoorPreps, group.pairActiveDoorPrepsCustom, "pairActiveDoorPrep", "pairActiveDoorPrepCustomTemplate", "pairActiveDoorPrepCustomTemplateName")) {
            updated = true;
        }

        if(autoFix(obj, group.pairFramePreps, group.pairFramePrepsCustom, "pairFramePrep", "pairFramePrepCustomTemplate", "pairFramePrepCustomTemplateName")) {
            updated = true;
        }

        if(autoFix(obj, group.pairInactiveDoorPreps, group.pairInactiveDoorPrepsCustom, "pairInactiveDoorPrep", "pairInactiveDoorPrepCustomTemplate", "pairInactiveDoorPrepCustomTemplateName")) {
            updated = true;
        }

        if(autoFix(obj, group.pairBothActiveDoorPreps || [], group.pairBothActiveDoorPrepsCustom || [], "pairBothActiveDoorPrep", "pairBothActiveDoorPrepCustomTemplate", "pairBothActiveDoorPrepCustomTemplateName")) {
            updated = true;
        }

        if(updated) {
            setValue(obj);
        }
    }, [props.isCreate, value, group]);

    const setValue2 = useCallback((input: Partial<PrepOption>) => {
        setValue(old => Object.assign({}, old, input));
    }, [setValue]);

    return {
        group, categories,
        value,
        setValue: setValue2,
    }
}

export function PrepMenuOptions(props: {
    value: PrepOption;
    category: number | null
    isCreate?: boolean;
    onChange(value: Partial<PrepOption>): void;
    categories: Category[];
    group: OptionGroup;
}) {
    const group = props.group;

    if (props.categories.length === 0) return <MenuItem key="loading">Loading...</MenuItem>

    if (props.category === null) {
        return <div key="error" style={{color: red["800"]}}>Please choose a category first</div>
    }

    switch(group.special) {
        case "no-options":
            return <PrepNoOptions value={props.value} onChange={props.onChange} />;
        case "door-and-frame-drop-down":
            return <DoorAndFrame value={props.value} onChange={props.onChange} group={group} />
        case "pair-only":
            return <PairOnly value={props.value} onChange={props.onChange} group={group} />
        case "single-drop-down":
            return <OneDropdown value={props.value} onChange={props.onChange} group={group} />
        default:
            return <AllProps value={props.value} onChange={props.onChange} group={group} />
    }
}

function autoFix(obj: PrepOption, preps: string[], customPreps: string[] | undefined, prepKey: string, customKey: string, customTemplateKey: string) {
    let updated = false;

    customPreps = customPreps || [];

    const altObjValue = obj[prepKey] === asPerTemplate ? customPrep : null;
    if(altObjValue && customKey.includes(altObjValue)) {
        return false;
    }

    if(!preps.includes(obj[prepKey]) && !customPreps.includes(obj[prepKey])) {

        if(preps.length === 0) {
            if(obj[prepKey] === noPrep) return false;
            obj[prepKey] = noPrep
        } else {
            obj[prepKey] = preps[0];
        }

        obj[customKey] = null;
        obj[customTemplateKey] = null;
        updated = true;
    }

    return updated;

}

const defaultGroup: OptionGroup = {
    categoryPath: ["Unknown"],
    singleFramePreps: [],
    singleFramePrepsCustom: [],
    pairFramePreps: [],
    pairFramePrepsCustom: [],
    singleActiveDoorPreps: [],
    singleActiveDoorPrepsCustom: [],
    pairActiveDoorPreps: [],
    pairActiveDoorPrepsCustom: [],
    pairInactiveDoorPreps: [],
    pairInactiveDoorPrepsCustom: [],
    pairBothActiveDoorPreps: [],
    pairBothActiveDoorPrepsCustom: [],
}
